import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { featureFlags } from '@shared-services/feature-flags';
import { Div } from 'client/widget-components/basicComponents';
import { DeprecatedPortal as Portal } from 'client/widget-components/components/Portal';
import React, { CSSProperties, FC, Ref, RefObject } from 'react';
import { SEARCH_VALUE_MIN_LENGTH } from '../constants';
import { HiddenSearchComponentsForEditorComputedStyles } from '../HiddenSearchComponentsForEditorComputedStyles';
import { CollectionsToSearch, SearchLayout, SearchPopupStyles } from '../types';
import { useCollectionSearch } from '../useCollectionSearch';
import { SearchPopupContentRound } from './SearchPopupContentRound';
import { SearchPopupContentSquare } from './SearchPopupContentSquare';

interface SearchPopupProps {
    inputRef: RefObject<HTMLInputElement> | null;
    widgetId: string;
    placeholder?: string;
    layout: SearchLayout;
    styles?: SearchPopupStyles;
    collectionsToSearch: CollectionsToSearch;
    animatedBlockRef?: Ref<HTMLDivElement>;
    animationState: string;
    closeSearchPopup: () => void;
}

export const SearchPopup: FC<SearchPopupProps> = ({
    inputRef,
    widgetId,
    placeholder,
    layout,
    styles,
    collectionsToSearch,
    closeSearchPopup,
    animatedBlockRef,
    animationState,
}) => {
    const {
        inputValue,
        isDropdownOpen,
        isLoading,
        collections,
        onInputChange,
        onInputFocus,
        clearSearchState,
        fetchMore,
    } = useCollectionSearch({ collectionsToSearch });
    const currentLayout = layout || SearchLayout.ROUND;

    const onClosePopup = () => {
        clearSearchState();
        closeSearchPopup();
    };

    const isResultsShown =
        isDropdownOpen && inputValue.length >= SEARCH_VALUE_MIN_LENGTH;

    const isRoundLayoutEnabled = featureFlags.getBoolean(
        'runtime.ssr.search.layout.enabled',
        false
    );

    return (
        <Portal>
            <Div
                styles={searchPopupDefaultStyles}
                data-grab={`collection-search-widget-portal-${widgetId}`}
            >
                <Div
                    style={transitionOverlayStyles[animationState]}
                    styles={overlayDefaultStyles}
                    onClick={onClosePopup}
                />
                {isRoundLayoutEnabled && currentLayout === SearchLayout.ROUND ? (
                    <SearchPopupContentRound
                        inputRef={inputRef}
                        styles={styles}
                        placeholder={placeholder}
                        inputValue={inputValue}
                        onInputChange={onInputChange}
                        onInputFocus={onInputFocus}
                        onClosePopup={onClosePopup}
                        collections={collections}
                        isResultsShown={isResultsShown}
                        isLoading={isLoading}
                        fetchMore={fetchMore}
                        animatedBlockRef={animatedBlockRef}
                        animationState={animationState}
                    />
                ) : (
                    <SearchPopupContentSquare
                        inputRef={inputRef}
                        styles={styles}
                        placeholder={placeholder}
                        inputValue={inputValue}
                        onInputChange={onInputChange}
                        onInputFocus={onInputFocus}
                        onClosePopup={onClosePopup}
                        collections={collections}
                        isResultsShown={isResultsShown}
                        isLoading={isLoading}
                        fetchMore={fetchMore}
                        animatedBlockRef={animatedBlockRef}
                        animationState={animationState}
                    />
                )}

                <HiddenSearchComponentsForEditorComputedStyles
                    styles={styles}
                />
            </Div>
        </Portal>
    );
};

const searchPopupDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 200,
    },
};

const overlayDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'none',
        background: 'rgba(0, 0, 0, 0.4)',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '100vh',
    },
};

const transitionOverlayStyles: Record<string, CSSProperties> = {
    entering: { display: 'block' },
    entered: { display: 'block' },
    exiting: { display: 'none' },
    exited: { display: 'none' },
};
